import React, { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import {
  useTable,
  usePagination,
  useGlobalFilter,
  useSortBy,
} from "react-table";

import "bootstrap/dist/css/bootstrap.min.css";
import { SERVER_BASE } from "../config/server";
import apiClient from "../utils/apiClient";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import Swal from "sweetalert2";

const DepartmentProformaX = ({ accessOfDepartments }) => {
  const [departments, setDepartments] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [date, setDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [tableData, setTableData] = useState([]);

  // =====================================================
  // Department Options
  // =====================================================

  useEffect(() => {
    if (accessOfDepartments) {
      const deptArray = accessOfDepartments
        .split(",")
        .map((dept) => ({
          value: dept.trim(),
          label: dept.trim(),
        }));

      setDepartments(deptArray);
    }
  }, [accessOfDepartments]);

  // =====================================================
  // Fetch Report
  // =====================================================

const handleGetReport = async () => {
  try {
    // ============================================
    // Validations
    // ============================================

    if (!selectedDepartment) {
      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "warning",
        title: "Please select Department",
        showConfirmButton: false,
        timer: 3000,
      });

      return;
    }

    if (!date) {
      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "warning",
        title: "Please select Date",
        showConfirmButton: false,
        timer: 3000,
      });

      return;
    }

    // ============================================
    // Date Validation
    // ============================================

    const minDate = new Date("2025-04-01");
    const selectedDate = new Date(date);

    if (selectedDate < minDate) {
      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "warning",
        title: "Date cannot be less than 01-04-2025",
        showConfirmButton: false,
        timer: 3000,
      });

      return;
    }

    // ============================================
    // API Call
    // ============================================

    setLoading(true);

    const response = await apiClient.get(
      `${SERVER_BASE}/department-proforma-x`,
      {
        params: {
          department: selectedDepartment.value,
          date,
        },
      }
    );

    setTableData(response.data || []);

  } catch (error) {
    console.error("Error fetching report:", error);

    Swal.fire({
      toast: true,
      position: "top-end",
      icon: "error",
      title: "Failed to fetch report",
      showConfirmButton: false,
      timer: 3000,
    });

  } finally {
    setLoading(false);
  }
};
const exportToExcel = async () => {
  try {
    const workbook = new ExcelJS.Workbook();

    const worksheet = workbook.addWorksheet(
      "Department Proforma X"
    );

    // =====================================================
    // Border Style
    // =====================================================

    const borderStyle = {
      top: { style: "thin" },
      left: { style: "thin" },
      bottom: { style: "thin" },
      right: { style: "thin" },
    };

    // =====================================================
    // Column Widths
    // =====================================================

    worksheet.columns = [
      { width: 8 },   // 1 - Sr No
      { width: 38 },  // 2 - Item Name
      { width: 12 },  // 3 - UOM
      { width: 18 },  // 3A - Acquisition Date
      { width: 18 },  // 4 - Opening
      { width: 14 },  // 5 - Addition
      { width: 18 },  // 6 - Deduction
      { width: 18 },  // 7 - Balance
      { width: 22 },  // 8 - Gross
      { width: 22 },  // 9 - Depreciation
      { width: 22 },  // 10 - Net Value
    ];

    // =====================================================
    // Main Heading
    // =====================================================

    worksheet.mergeCells("A1:K1");

    worksheet.getCell("A1").value = "PROFORMA - X";

    worksheet.getCell("A1").font = {
      bold: true,
      size: 12,
    };

    worksheet.getCell("A1").alignment = {
      horizontal: "right",
      vertical: "middle",
    };

    // =====================================================
    // Company Name
    // =====================================================

    worksheet.mergeCells("A2:K2");

    worksheet.getCell("A2").value =
      "RADHA SOAMI SATSANG BEAS";

    worksheet.getCell("A2").font = {
      bold: true,
      size: 15,
    };

    worksheet.getCell("A2").alignment = {
      horizontal: "center",
      vertical: "middle",
    };

    // =====================================================
    // Location
    // =====================================================

    worksheet.mergeCells("A3:K3");

    worksheet.getCell("A3").value =
      "RADHA SOAMI SATSANG BEAS, JAIPUR";

    worksheet.getCell("A3").font = {
      bold: true,
      size: 18,
    };

    worksheet.getCell("A3").alignment = {
      horizontal: "center",
      vertical: "middle",
    };

    worksheet.getRow(2).height = 24;
    worksheet.getRow(3).height = 28;

    // =====================================================
    // Department Row
    // =====================================================

    worksheet.getCell("A5").value = "DEPARTMENT :";

    worksheet.getCell("A5").font = {
      bold: true,
      size: 11,
    };

    worksheet.mergeCells("B5:D5");

    worksheet.getCell("B5").value =
      selectedDepartment?.label || "-";

    worksheet.getCell("B5").font = {
      bold: true,
      size: 11,
    };

    worksheet.getRow(5).height = 22;

    // =====================================================
    // Opening Date - 1
    // =====================================================

    const openingDate = tableData?.[0]?.openingDate;

    let previousDate = "";

    if (openingDate) {
      const tempDate = new Date(openingDate);

      tempDate.setDate(tempDate.getDate() - 1);

      previousDate =
        tempDate.toLocaleDateString("en-GB");
    }

    // =====================================================
    // Header Row
    // =====================================================

    const headerRow = worksheet.addRow([
      "S. No.",
      "Item Name",
      "UOM",
      "Date of Acquisition on",

      `As per last Verification Report as on ${previousDate} as per ledger`,

      "Addition",

      "Reduction due to transfer / Condemnation",

      "Balance (4+5-6)",

      "Gross value as on 31.03.2026",

      "Depreciation To be filled by Zonal Accounts Department",

      "Net value as on 31.03.2026",
    ]);

    headerRow.height = 60;

    headerRow.eachCell((cell) => {
      cell.font = {
        bold: true,
        size: 10,
      };

      cell.alignment = {
        vertical: "middle",
        horizontal: "center",
        wrapText: true,
      };

      cell.border = borderStyle;
    });

    // =====================================================
    // Number Row
    // =====================================================

    const numberRow = worksheet.addRow([
      "1",
      "2",
      "3",
      "3A",
      "4",
      "5",
      "6",
      "7",
      "8",
      "9",
      "10",
    ]);

    numberRow.height = 22;

    numberRow.eachCell((cell) => {
      cell.font = {
        bold: true,
        italic: true,
        size: 10,
      };

      cell.alignment = {
        vertical: "middle",
        horizontal: "center",
      };

      cell.border = borderStyle;
    });

    // =====================================================
    // Table Data
    // =====================================================

    tableData.forEach((item, index) => {
      const acquisitionDates =
        item.acquisitionDate?.length > 0
          ? item.acquisitionDate
              .map((date) =>
                date
                  ? new Date(date).toLocaleDateString(
                      "en-GB"
                    )
                  : "-"
              )
              .join("\n")
          : "-";

      const openingQty =
        Number(item.netOpeningQty || 0);

      const additionQty =
        Number(item.addQty || 0);

      const deductionQty =
        Number(item.lessQty || 0);

      const balance =
        openingQty +
        additionQty -
        deductionQty;

      const openingValue =
        Number(item.openingValue || 0);

      const addValue =
        Number(item.addValue || 0);

      const lessValue =
        Number(item.lessValue || 0);

      const grossValue =
        openingValue +
        addValue -
        lessValue;

      const row = worksheet.addRow([
        // 1
        index + 1,

        // 2
        item.printName || "-",

        // 3
        item.uom || "-",

        // 3A
        acquisitionDates,

        // 4
        openingQty === 0
          ? "-"
          : openingQty,

        // 5
        additionQty === 0
          ? "-"
          : additionQty,

        // 6
        deductionQty === 0
          ? "-"
          : deductionQty,

        // 7
        balance === 0
          ? "-"
          : balance,

        // 8
        grossValue === 0
          ? "-"
          : grossValue,

        // 9
        "",

        // 10
        "",
      ]);

      row.height = 28;

      row.eachCell((cell) => {
        cell.font = {
          size: 10,
        };

        cell.alignment = {
          vertical: "middle",
          horizontal: "center",
          wrapText: true,
        };

        cell.border = borderStyle;
      });

      // Left align item name
      row.getCell(2).alignment = {
        vertical: "middle",
        horizontal: "left",
        wrapText: true,
      };

      // Center UOM
      row.getCell(3).alignment = {
        vertical: "middle",
        horizontal: "center",
        wrapText: true,
      };

      // Number format for gross value column
      row.getCell(9).numFmt = "#,##0.00";
    });

    // =====================================================
    // Signature Section
    // =====================================================

    const lastRow = worksheet.lastRow.number + 4;

    worksheet.getCell(`A${lastRow}`).value =
      "Head of Department / Secretary";

    worksheet.getCell(`A${lastRow}`).font = {
      bold: true,
      size: 11,
    };

    worksheet.getCell(`I${lastRow}`).value =
      "Secretary";

    worksheet.getCell(`I${lastRow}`).font = {
      bold: true,
      size: 11,
    };

    // =====================================================
    // Download Excel
    // =====================================================

    const buffer =
      await workbook.xlsx.writeBuffer();

    saveAs(
      new Blob([buffer]),
      `Department_Proforma_X.xlsx`
    );

  } catch (error) {
    console.error(
      "Excel Export Error:",
      error
    );

    Swal.fire({
      toast: true,
      position: "top-end",
      icon: "error",
      title: "Failed to export excel",
      showConfirmButton: false,
      timer: 3000,
    });
  }
};
  // =====================================================
  // Table Columns
  // =====================================================

  const columns = useMemo(
    () => [
      {
        Header: "Sr. No",
        Cell: ({ row }) => row.index + 1,
      },

      {
        Header: "Item Name",
        accessor: "printName",
      },

      {
        Header: "Date of Acquisition",
        accessor: "acquisitionDate",
        Cell: ({ value }) => {
          if (!value || value.length === 0) {
            return "-";
          }

return (
  <div>
    {value.map((date, index) => (
      <div key={index}>
        {date
          ? new Date(date).toLocaleDateString("en-GB")
          : "-"}
      </div>
    ))}
  </div>
);
        },
      },

      {
        Header: "Opening Qty",
        accessor: "netOpeningQty",
      },

      {
        Header: "Addition",
        accessor: "addQty",
      },

      {
        Header: "Deduction",
        accessor: "lessQty",
      },

{
        Header: "Balance",
        Cell: ({ row }) => {
          const opening = Number(row.original.netOpeningQty || 0);
          const add = Number(row.original.addQty || 0);
          const less = Number(row.original.lessQty || 0);

          return opening + add - less;
        },
      },

      {
        Header: "Value",
        Cell: ({ row }) => {
          const openingValue = Number(row.original.openingValue || 0);
          const addValue = Number(row.original.addValue || 0);
          const lessValue = Number(row.original.lessValue || 0);

          const value = openingValue + addValue - lessValue;

          return value.toLocaleString("en-IN", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          });
        },
      },

      {
        Header: "UOM",
        accessor: "uom",
      }
    ],
    []
  );

  // =====================================================
  // React Table
  // =====================================================

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    nextPage,
    previousPage,
    canNextPage,
    canPreviousPage,
    pageOptions,
    state,
    setGlobalFilter,
  } = useTable(
    {
      columns,
      data: tableData,
      initialState: {
        pageSize: 10,
      },
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const { pageIndex, globalFilter } = state;

  return (
    <div className="container-fluid mt-3">
      <div className="card shadow-sm">
        <div className="card-header">
          <h5 className="mb-0">Department Proforma X</h5>
        </div>

        <div className="card-body">
          {/* Filters */}
          <div className="row align-items-end">
            {/* Department */}
            <div className="col-md-4 mb-3">
              <label className="form-label">Department</label>

              <Select
                options={departments}
                value={selectedDepartment}
                onChange={setSelectedDepartment}
                placeholder="Select Department"
                isClearable
              />
            </div>

            {/* Date */}
            <div className="col-md-3 mb-3">
              <label className="form-label">Date</label>

              <input
                type="date"
                className="form-control"
                value={date}
                onChange={(e) => setDate(e.target.value)}
              />
            </div>

            {/* Get Button */}
            <div className="col-md-2 mb-3">
              <button
                className="btn btn-primary w-100"
                onClick={handleGetReport}
                disabled={loading}
              >
                {loading ? "Loading..." : "Get"}
              </button>
            </div>
            <div className="col-md-2 mb-3">
  <button
    className="btn btn-success w-100"
    onClick={exportToExcel}
    disabled={tableData.length === 0}
  >
    Export Excel
  </button>
</div>
          </div>

          {/* Search */}
          <div className="row mt-3">
            <div className="col-md-4">
              <input
                type="text"
                className="form-control"
                placeholder="Search..."
                value={globalFilter || ""}
                onChange={(e) => setGlobalFilter(e.target.value)}
              />
            </div>
          </div>

          {/* Table */}
          <div style={{ overflowX: "auto", whiteSpace: "nowrap" }}>
            <table
              className="table table-bordered table-striped mt-3"
              {...getTableProps()}
              style={{ fontSize: "14px" }}
            >
              <thead className="table-light">
                {headerGroups.map((headerGroup) => (
                  <tr {...headerGroup.getHeaderGroupProps()}>
                    {headerGroup.headers.map((column) => (
                      <th
                        {...column.getHeaderProps(
                          column.getSortByToggleProps()
                        )}
                        style={{ cursor: "pointer" }}
                      >
                        {column.render("Header")}

                        <span>
                          {column.isSorted
                            ? column.isSortedDesc
                              ? " 🔽"
                              : " 🔼"
                            : ""}
                        </span>
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>

              <tbody {...getTableBodyProps()}>
                {page.length > 0 ? (
                  page.map((row) => {
                    prepareRow(row);

                    return (
                      <tr {...row.getRowProps()}>
                        {row.cells.map((cell) => (
                          <td
                            {...cell.getCellProps()}
                            style={{ verticalAlign: "top" }}
                          >
                            {cell.render("Cell")}
                          </td>
                        ))}
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td
                      colSpan={columns.length}
                      className="text-center"
                    >
                      No Data Found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="d-flex justify-content-between align-items-center mt-3">
            <div>
              Page{" "}
              <strong>
                {pageIndex + 1} of {pageOptions.length}
              </strong>
            </div>

            <div className="d-flex gap-2">
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => previousPage()}
                disabled={!canPreviousPage}
              >
                Previous
              </button>

              <button
                className="btn btn-secondary btn-sm"
                onClick={() => nextPage()}
                disabled={!canNextPage}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DepartmentProformaX;